import {
  CLEAR_PURCHASED_USER_CART_ITEMS,
  SYNC_USER_CART,
  SAVE_ALL_USER_CART_ITEMS_FOR_LATER,
  UPDATE_USER_CART_ITEM,
  UNDO_USER_CART_ITEMS_DELETE,
  RESET_USER_CART,
  SAVE_USER_CART_ITEMS_FOR_LATER,
  DELETE_USER_CART_ITEMS,
  HARD_DELETE_USER_CART_ITEMS
} from 'actions/actionConstants'
import { API_CALL } from './actionConstants'
import {
  FETCH_USER_CART
} from './apiActionConstants'
import { AppAction } from './types/ActionTypes'
import { selectLoggedIn } from 'selectors/accountSelectors'

import { getUserCartAbandonableItems } from 'selectors/userCart'
import { prepareItem } from 'lib/checkout/userCart'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import { isTwoOneWaysItem } from 'components/Pages/CartReviewPage/CartReviewUtils'
import { suppressCartAutoDropdown } from 'components/Pages/UserCartPage/UserCartPreview/cartAutoDropdownCookie'
import { getUserCart } from 'api/cart/userCart'
import { getCartMachineActor } from 'machines/cart/MultiCartMachineProvider'
import { selectItemsInActiveCart } from 'machines/cart/selectors'

const DEFAULT_USER_CART_DELETE_DELAY_MS = 5000

export function addToUserCart(
  items: Array<App.Checkout.AnyItem>,
  skipNotification: boolean = false,
  requestConnectedRooms: boolean = false
): AppAction {
  return () => {
    // Always use XState machine - multi-cart is always enabled
    getCartMachineActor()?.send({
      type: 'add items to active cart',
      items: items.map(prepareItem),
      requestConnectedRooms,
      skipNotification
    })

    // Suppress auto preview dropdown for 1 day after adding items to prevent it from opening immediately
    if (items.length > 0) {
      suppressCartAutoDropdown()
    }

    // If any of the new items are flights, save all other flights for later
    // when adding two one-way flights or when existing two one-way flights are in the cart
    const hasFlightItems = items.some(isFlightItem)
    if (hasFlightItems) {
      const cartMachineActor = getCartMachineActor()
      if (cartMachineActor) {
        const snapshot = cartMachineActor.getSnapshot()
        const existingCartItems = selectItemsInActiveCart(snapshot)
        const hasTwoOneWaysFlightItem = items.some(item => isFlightItem(item) && isTwoOneWaysItem(item))

        const existingFlightItems = existingCartItems
          .map(multiCartItem => multiCartItem.item)
          .filter(isFlightItem)
          .filter(item => !items.some(newItem => newItem.itemId === item.itemId))

        existingFlightItems.forEach(item => {
          const multiCartItem = existingCartItems.find(mci => mci.item.itemId === item.itemId)
          const isSaveForLater = multiCartItem?.state === 'save_for_later'
          if (!isSaveForLater && (isTwoOneWaysItem(item) || hasTwoOneWaysFlightItem)) {
            // Use machine to save for later
            cartMachineActor.send({
              type: 'update cart items save for later',
              itemIds: [item.itemId],
              saveForLater: true
            })
          }
        })
      }
    }
  }
}

export function clearUserCartPurchased(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    dispatch({
      type: CLEAR_PURCHASED_USER_CART_ITEMS,
      purchasedItemIds: new Set(state.checkout.cart.items.map(item => item.itemId)),
    })
  }
}

export function moveCheckoutItemsToUserCart(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    // Connected rooms are handled via requestConnectedRooms parameter in addToUserCart
    dispatch(addToUserCart(getUserCartAbandonableItems(state, state.checkout.cart.items)))
  }
}

export function syncUserCart(userCartState: App.UserCartState): AppAction {
  return {
    type: SYNC_USER_CART,
    userCartState,
  }
}

export function setCartGroupItemsSaveForLater(itemIds: Array<string>, saveForLater: boolean): AppAction {
  return {
    type: SAVE_USER_CART_ITEMS_FOR_LATER,
    itemIds,
    saveForLater,
  }
}

export function userCartItemsSaveForLater(itemIds: Array<string>, intendedSaveForLater?: boolean): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const itemStates = state.auth.userCart.itemStates
    // if provided, use the value otherwise work as a 'toggle' based on the first items status
    const intendedSaveForLaterState = intendedSaveForLater ?? !itemStates[itemIds[0]]?.saveForLater

    const itemsToSaveForLater = itemIds.filter(itemId => !!(itemStates[itemId]?.saveForLater) !== intendedSaveForLaterState)

    if (itemsToSaveForLater.length === 0) {
      return
    }

    dispatch({
      type: SAVE_USER_CART_ITEMS_FOR_LATER,
      itemIds: itemsToSaveForLater,
      saveForLater: intendedSaveForLaterState,
    })
  }
}

export function saveAllUserCartItemsForLater(): AppAction {
  return {
    type: SAVE_ALL_USER_CART_ITEMS_FOR_LATER,
  }
}

export function userCartItemUpdate(item: App.Checkout.AnyItem): AppAction {
  return {
    type: UPDATE_USER_CART_ITEM,
    item,
  }
}

export function userCartItemsDelete(itemIds: Array<string>): AppAction {
  return (dispatch, getState) => {
    dispatch({
      type: DELETE_USER_CART_ITEMS,
      itemIds,
    })
    setTimeout(() => {
      const state = getState()
      const itemsToDelete = itemIds.filter(itemId => state.auth.userCart.itemStates[itemId]?.deleted)
      dispatch(userCartItemsHardDelete(itemsToDelete))
    }, DEFAULT_USER_CART_DELETE_DELAY_MS)
  }
}

export function userCartItemsHardDelete(itemIds: Array<string>): AppAction {
  return {
    type: HARD_DELETE_USER_CART_ITEMS,
    itemIds,
  }
}

export function userCartItemUndo(itemIds: Array<string>): AppAction {
  return {
    type: UNDO_USER_CART_ITEMS_DELETE,
    itemIds,
  }
}

export function fetchUserCart({ token }: { token?: string } = {}): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const isLoggedIn = selectLoggedIn(state)
    const hasItems = Object.keys(state.auth.userCart.items).length > 0
    const isFetching = state.auth.userCart.fetching
    if (!isLoggedIn || hasItems || isFetching) { return }

    dispatch({
      type: API_CALL,
      api: FETCH_USER_CART,
      request: () => getUserCart(token),
    })
  }
}

export function deleteAllUserCartItems(): AppAction {
  return {
    type: RESET_USER_CART,
  }
}
